import React from 'react';
import TextInput from '~/components/TextInput';
import InputContainer from '~/components/InputContainer';
import Toggle from '~/components/Toggle';
import Select from '~/components/Select';
import SectionPicker from '~/components/SectionPicker';
import CodePane from '~/components/customquestions/CodePane';
import CodeReview from '~/components/customquestions/CodeReview';
import CustomQuestion from '~/components/forms/questions/CustomQuestion';
import ThemedForm from '~/components/forms/ThemedForm';
import EditCustomQuestionForm from '~/components/forms/edit/EditCustomQuestionForm';
import AssistantChatConversation from '~/components/assistant/AssistantChatConversation';
import { CUSTOM_QUESTION_ICON_KEYS } from '~/lib/CustomQuestions/CustomQuestionIcons';
import {
    DEFAULT_SECTION_TYPES,
    SECTION_TYPE_LABELS,
} from '~/lib/CustomQuestions/CustomQuestionApi';

/*
 * The pieces of the custom question editor, each handed the state from
 *  useCustomQuestionEditor, for the page and modal layouts to arrange.
 */

const FLAG_LABELS = {
    noLabel: 'The question draws its own heading',
    noRequired: 'There is no answer to require',
    alwaysRequired: 'An answer is always required',
    excludeFromAnswers: 'Display only, no answer to record',
    allowPrefill: 'A logged-in client can have a past answer filled in',
    oncePerTransaction: 'Only charge for this once per submission',
    rendersAnswer: 'Render its own answer when an admin reads a submission',
};

// What the chat input cycles through, in place of the general setup prompts
const CHAT_PLACEHOLDERS = [
    'Create a 1 to 10 slider with labels at each end...',
    'Create a color picker with my brand colors...',
    'Create seat from a map of the room...',
];

/**
 * Assistant or Advanced, when the assistant is on.
 */
export const ModePicker = ({ editor, className = 'mb-4' }) => {
    const { isAssistantEnabled, activeMode, setActiveMode } = editor;

    if (!isAssistantEnabled) {
        return null;
    }

    return (
        <SectionPicker
            value={activeMode}
            onChange={setActiveMode}
            options={[
                { value: 'assistant', label: 'Assistant' },
                { value: 'advanced', label: 'Advanced' },
            ]}
            slim={true}
            className={className}
        />
    );
};

export const AssistantChat = ({ editor }) => {
    const {
        isAssistantEnabled,
        activeMode,
        conversationRef,
        chatStartingMessages,
        onAssistantMessages,
        isNew,
        customQuestionId,
    } = editor;

    return (
        <>
            {/* Kept mounted while on Advanced so the conversation
                        survives switching tabs */}
            {isAssistantEnabled && (
                <div className={activeMode === 'assistant' ? '' : 'hidden'}>
                    <AssistantChatConversation
                        ref={conversationRef}
                        startingMessages={chatStartingMessages}
                        showChat={true}
                        isNewBusiness={false}
                        hideRecentConvos={true}
                        onMessagesUpdate={onAssistantMessages}
                        context={
                            isNew
                                ? 'The business is writing a new custom question in the custom question editor. Create it with update_custom_question.'
                                : `The business is editing custom question ID=${customQuestionId} in the custom question editor. Change it with update_custom_question, custom_question_id=${customQuestionId}.`
                        }
                        source="CustomQuestionEditPage"
                        placeholders={CHAT_PLACEHOLDERS}
                        // Starts short and grows with the conversation, scrolling
                        //  once it reaches the max
                        className="flex flex-col bg-white rounded-[22px] px-4 pt-4 min-h-[200px] max-h-[420px] [&>.assistant-chatbox]:flex-1 [&>.assistant-chatbox]:min-h-0 [&>div:last-child]:mt-auto"
                    />
                </div>
            )}
        </>
    );
};

// The default, nearly invisible input border. The modal's panel class gives
//  inputs a visible gray-200 one, which these sit outside any panel to avoid
const FIELD_BORDER = '!border-gray-100 focus:!border-primary-700';

/**
 * Name, icon and description.
 */
export const DetailsFields = ({ editor }) => {
    const { draft, set } = editor;

    return (
        <>
            <div className="flex gap-x-4">
                <TextInput
                    className="flex-1"
                    label="Name"
                    inputClassName={`w-full !max-w-full ${FIELD_BORDER}`}
                    value={draft.name}
                    onChange={name => set({ name })}
                    autoFocus={!draft.name}
                />
                <Select
                    label="Icon"
                    options={CUSTOM_QUESTION_ICON_KEYS.map(key => ({
                        value: key,
                        label: key,
                    }))}
                    inputClassName={FIELD_BORDER}
                    value={draft.icon}
                    onChange={icon => set({ icon })}
                />
            </div>

            <InputContainer className="mt-3" label="Description">
                <textarea
                    className={`w-full !max-w-full h-[84px] resize-y ${FIELD_BORDER}`}
                    value={draft.description}
                    onChange={e => set({ description: e.target.value })}
                />
            </InputContainer>
        </>
    );
};

/**
 * The HTML/CSS/JS panes, the validator's review and the additional options.
 */
export const CodeEditor = ({ editor, className = 'mt-6' }) => {
    const {
        draft,
        set,
        review,
        activePane,
        setActivePane,
        activeConfig,
        setActiveConfig,
        showAdditional,
        setShowAdditional,
    } = editor;

    const sectionTypes = (
        <SectionTypes
            value={draft.flags?.sections}
            onChange={sections =>
                set({
                    flags: {
                        ...(draft.flags || {}),
                        sections,
                    },
                })
            }
        />
    );

    const flagRows = Object.keys(FLAG_LABELS).map((flag, idx) => (
        <div
            key={flag}
            className={`flex items-center justify-between py-3 pr-4 ${idx > 0 ? 'border-t' : ''}`}
        >
            <div className="text-sm">{FLAG_LABELS[flag]}</div>
            <Toggle
                enabled={!!draft.flags?.[flag]}
                label={FLAG_LABELS[flag]}
                setEnabled={enabled =>
                    set({
                        flags: {
                            ...(draft.flags || {}),
                            [flag]: enabled,
                        },
                    })
                }
            />
        </div>
    ));

    return (
        <>
            <div className={className}>
                <SectionPicker
                    value={activePane}
                    onChange={setActivePane}
                    options={[
                        { value: 'html', label: 'HTML' },
                        { value: 'css', label: 'CSS' },
                        { value: 'js', label: 'JS' },
                    ]}
                    slim={true}
                    className="mb-2"
                />

                <CodePane
                    language={activePane}
                    value={draft[activePane]}
                    onChange={value => set({ [activePane]: value })}
                />
            </div>

            <CodeReview review={review} className="px-4" />

            <div className="mt-2">
                <button
                    type="button"
                    className="text-sm text-primary-700 hover:underline"
                    onClick={() => setShowAdditional(!showAdditional)}
                >
                    {showAdditional ? '− ' : '+ '}
                    {showAdditional
                        ? 'Hide Additional Options'
                        : 'Show Additional Options'}
                </button>
            </div>

            {showAdditional && (
                <div className="mt-4">
                    <SectionPicker
                        value={activeConfig}
                        onChange={setActiveConfig}
                        options={[
                            {
                                value: 'settings',
                                label: 'Settings',
                            },
                            {
                                value: 'answer',
                                label: 'Answer',
                            },
                            {
                                value: 'behavior',
                                label: 'Behavior',
                            },
                        ]}
                        slim={true}
                        className="mb-2"
                    />

                    {activeConfig === 'settings' && (
                        <div className="panel p-4 !max-w-full">
                            <div className="text-gray-500 text-sm mb-2">
                                What the business is asked when they put this on
                                a form, as a JSON list of fields.
                            </div>
                            <CodePane
                                language="js"
                                minHeight="160px"
                                value={draft.settings_schema}
                                onChange={value =>
                                    set({
                                        settings_schema: value,
                                    })
                                }
                            />
                        </div>
                    )}

                    {activeConfig === 'answer' && (
                        <div className="panel p-4 !max-w-full">
                            <div className="text-gray-500 text-sm mb-2">
                                The summary is what emails, the submission PDF
                                and exports show, since none of those can run
                                this question's code.
                            </div>
                            <CodePane
                                language="js"
                                minHeight="140px"
                                value={draft.answer_schema}
                                onChange={value =>
                                    set({
                                        answer_schema: value,
                                    })
                                }
                            />
                        </div>
                    )}

                    {activeConfig === 'behavior' && (
                        <>
                            <div className="panel-label mt-6 !mb-1">
                                Where this can be added
                            </div>
                            <div className="panel pl-4 !max-w-full">
                                {sectionTypes}
                            </div>

                            <div className="panel-label mt-6 !mb-1">
                                Additional Options
                            </div>
                            <div className="panel pl-4 !max-w-full">
                                {flagRows}
                            </div>
                        </>
                    )}
                </div>
            )}
        </>
    );
};

/**
 * The editor's two columns, the same on the page and in the modal: the name,
 *  icon and description, then Assistant/Advanced with the chat or the code,
 *  beside the preview and Save.
 */
export const EditorColumns = ({ editor }) => {
    const { activeMode, isAssistantEnabled } = editor;

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div>
                <DetailsFields editor={editor} />
                <ModePicker editor={editor} className="mt-6 mb-4" />
                <AssistantChat editor={editor} />

                {(activeMode === 'advanced' || !isAssistantEnabled) && (
                    <CodeEditor
                        editor={editor}
                        className={isAssistantEnabled ? '' : 'mt-6'}
                    />
                )}
            </div>

            <div>
                <PreviewPanel editor={editor} />
                <EditorActions editor={editor} />
            </div>
        </div>
    );
};

/**
 * The live preview and the settings panel driving it.
 *
 * The preview is the real runtime - the same sandboxed frame the form uses,
 *  handed what is in the editor rather than going through the registry, which
 *  holds what is saved.
 */
export const PreviewPanel = ({ editor }) => {
    const {
        draft,
        resolvedPreviewSettings,
        previewDefinition,
        previewSettings,
        setPreviewSettings,
    } = editor;

    return (
        <>
            <div className="panel-label !mb-1">Preview</div>
            <div className="panel p-4">
                <ThemedForm styles={{}} scopeRoot={true}>
                    <CustomQuestion
                        label={draft.name}
                        value={undefined}
                        setValue={() => {}}
                        settings={resolvedPreviewSettings}
                        id="preview"
                        mode="fill"
                        definitionOverride={previewDefinition}
                    />
                </ThemedForm>
            </div>

            <div className="panel-label mt-6 !mb-1">Preview Settings</div>
            <div className="panel p-4 [&>div]:mt-0 [&>div>:first-child]:mt-0">
                <PreviewSettings
                    definition={previewDefinition}
                    settings={previewSettings}
                    setSettings={setPreviewSettings}
                />
            </div>
            <div className="text-gray-500 text-sm mt-2 pl-4 pr-4">
                What a business sees when they add this question to a form. Edit
                to see how the preview responds.
            </div>
        </>
    );
};

/**
 * Save, and Delete once it has been saved.
 */
export const EditorActions = ({ editor }) => {
    const {
        saveQuestion,
        isSaving,
        hasBlockingErrors,
        isNew,
        doDelete,
        isDeleting,
    } = editor;

    return (
        <div className="mt-6 flex items-center gap-x-3">
            <button
                type="button"
                className="btn-pill"
                onClick={saveQuestion}
                disabled={isSaving || hasBlockingErrors}
            >
                {isSaving ? 'Saving...' : 'Save Custom Question'}
            </button>
            {!isNew && (
                <button
                    type="button"
                    className="btn-red-pill"
                    onClick={doDelete}
                    disabled={isDeleting}
                >
                    {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
            )}
        </div>
    );
};

/**
 * Which kinds of section this question can be added to.
 *
 * The Add Element panel offers it in each one picked, so leaving none picked
 *  would make the question impossible to add anywhere - the last one can't be
 *  turned off.
 */
const SectionTypes = ({ value, onChange }) => {
    const chosen = value?.length ? value : DEFAULT_SECTION_TYPES;

    function toggle(type, enabled) {
        const next = enabled
            ? [...chosen, type]
            : chosen.filter(t => t !== type);

        onChange(next.length ? next : chosen);
    }

    return (
        <div>
            {Object.keys(SECTION_TYPE_LABELS).map((type, idx) => {
                const isOn = chosen.includes(type);
                const isLastOn = isOn && chosen.length === 1;

                return (
                    <div
                        key={type}
                        className={`flex items-center justify-between py-3 pr-4 ${idx > 0 ? 'border-t' : ''}`}
                    >
                        <div className="text-sm">
                            {SECTION_TYPE_LABELS[type]}
                        </div>
                        <Toggle
                            enabled={isOn}
                            disabled={isLastOn}
                            label={`Can be added to ${SECTION_TYPE_LABELS[type]}`}
                            setEnabled={enabled => toggle(type, enabled)}
                        />
                    </div>
                );
            })}
        </div>
    );
};

/**
 * The real settings panel, driven by the schema being edited beside it, so an
 *  author sees what the business will be asked.
 */
const PreviewSettings = ({ definition, settings, setSettings }) => {
    if (!definition?.fields?.length) {
        return (
            <div className="text-gray-500 text-sm">
                This question has no settings.
            </div>
        );
    }

    return (
        <EditCustomQuestionForm
            settings={{ ...settings, customQuestionId: definition.id }}
            setSettings={setSettings}
            definitionOverride={definition}
        />
    );
};
